'use client';

/**
 * NavRail Component Requirements:
 * 
 * 1. Visual Requirements:
 *    - Active item must use primary color styling
 *    - Hover states should provide visual feedback
 *    - Disabled items should have reduced opacity and no pointer events
 *    - Panels should animate in/out smoothly
 * 
 * 2. Layout Requirements:
 *    - Navigation rail is fixed width (w-16)
 *    - Docked panel has configurable width (default w-64)
 *    - Hover panel appears to right of rail with slight offset
 *    - Maintains full height of container
 * 
 * 3. Accessibility:
 *    - Nav items should be keyboard navigable
 *    - Tooltips should show labels on hover
 *    - Proper ARIA attributes for navigation
 *    - Focus management for panels
 * 
 * 4. Interaction Requirements:
 *    - Hover panels should have small delay before closing
 *    - Click active item to toggle its docked panel
 *    - Click non-active item to navigate to its href
 *    - Prevent interaction with disabled items
 */

/**
 * NavRail Component State Invariants:
 * 
 * State:
 * - dockedPanel: { itemId: string | null, isOpen: boolean }
 * - hoverPanelId: string | null
 * 
 * Invariants:
 * 1. Active State (dockedPanel.itemId):
 *    - Only one nav item can be active at a time
 *    - Active item must use primary color styling
 *    - Active item cannot have a hover panel (hoverPanelId !== dockedPanel.itemId)
 *    - Active item must stay in sync with current route
 * 
 * 2. Panel States:
 *    - A nav item can have either a hover panel OR a docked panel visible, never both
 *    - Hover panel can only exist for non-active, non-disabled items with panels
 *    - Docked panel can only exist for the active item when dockedPanel.isOpen is true
 * 
 * 3. State Transitions:
 *    - Hovering nav item: 
 *      - If item has panel and is not active: show hover panel
 *      - If item has no panel or is active: no hover panel
 *    - Clicking nav item:
 *      - If item was active: toggle docked panel
 *      - If item was not active: make active and show docked panel if it has one
 *      - Always clear hover panel state
 *    - URL changes:
 *      - Update active item based on current route
 *      - Maintain docked panel state for active item
 */

import * as React from 'react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { FileSpreadsheet } from 'lucide-react';
import { useNavRailStore } from './nav-rail-store';
import { createNavRailStore } from './nav-rail-store';

export interface NavItem {
  id: string;
  label: string;
  href?: string;
  onClick?: () => void;
  badgeCount?: number;
  badgeVariant?: 'default' | 'primary' | 'secondary' | 'destructive';
  divider?: boolean;
  target?: string;
  rel?: string;
  disabled?: boolean;
  panel?: React.ReactNode;
  panelWidth?: string;
  panelTitle?: string;
  icon?: React.ReactNode;
}

export interface NavRailProps {
  items: NavItem[];
  className?: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  footerClassName?: string;
  defaultActiveId?: string;
}

interface FooterItemProps extends Partial<NavItem> {
  children?: React.ReactNode;
}

/**
 * A simplified vertical navigation rail component with panels
 */
export function NavRail({
  items,
  className,
  header,
  footer,
  footerClassName,
  defaultActiveId
}: NavRailProps) {
  const pathname = usePathname();
  const { 
    activeNavItemId, 
    userPrefersPanelOpen, 
    hoveredItemId,
    hoverSidenavId,
    setPath,
    togglePanel,
    setHoveredItem,
    setNavItems
  } = useNavRailStore();

  // Initialize store with nav items - only update when structure actually changes
  const navStructure = React.useMemo(() => 
    items.map(item => ({
      id: item.id,
      path: item.href || '/',
      hasSidenav: !!item.panel
    })), 
    [items.map(item => `${item.id}:${item.href || '/'}:${!!item.panel}`).join('|')]
  );

  React.useEffect(() => {
    setNavItems(navStructure);
  }, [navStructure, setNavItems]);

  // Update path in store when pathname changes
  React.useEffect(() => {
    setPath(pathname);
  }, [pathname, setPath]);

  const handleItemClick = (item: NavItem, e: React.MouseEvent) => {
    if (item.disabled) {
      e.preventDefault();
      return;
    }

    // Clear hover state immediately when clicking
    setHoveredItem(null, true);

    // Only toggle panel if clicking active item
    if (item.id === activeNavItemId) {
      togglePanel();
      if (item.onClick) {
        item.onClick();
      }
      e.preventDefault(); // Prevent navigation when toggling panel
    } else if (item.onClick) {
      item.onClick();
    }
  };

  const handleItemMouseEnter = React.useCallback((item: NavItem) => {
    if (!item.disabled) {
      setHoveredItem(item.id, true);
    }
  }, [setHoveredItem]);

  const handleMouseLeave = React.useCallback(() => {
    setHoveredItem(null);
  }, [setHoveredItem]);

  const renderNavItem = (item: NavItem) => {
    const isActive = item.id === activeNavItemId;
    
    return (
      <React.Fragment key={item.id}>
        {item.divider && <div className="w-8 h-px bg-border my-1" />}
        <div 
          className="group"
          onMouseEnter={() => handleItemMouseEnter(item)}
          onMouseLeave={handleMouseLeave}
        >
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <NavRailItem
                    label={item.label}
                    isActive={isActive}
                    badge={renderBadge(item)}
                    disabled={item.disabled}
                    href={item.href}
                    onClick={(e: React.MouseEvent) => handleItemClick(item, e)}
                    target={item.target}
                    rel={item.rel}
                    icon={item.icon}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={10}>
                {item.label}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </React.Fragment>
    );
  };

  return (
    <div className={cn('flex h-full', className)}>
      {/* Navigation rail */}
      <div className="w-16 border-r flex flex-col bg-sidebar">
        {header && <div className="p-2 border-b">{header}</div>}
        
        <div className="flex-1 flex flex-col items-center py-4 gap-1">
          {items.map(renderNavItem)}
        </div>
        
        {footer && (
          <div className={cn("p-2 border-t mt-auto flex flex-col items-center gap-1", footerClassName)}>
            {footer}
          </div>
        )}
      </div>

      {/* Docked Panel */}
      {userPrefersPanelOpen && activeNavItemId && (() => {
        const activeItem = items.find(item => item.id === activeNavItemId);
        if (!activeItem?.panel) return null;
        
        return (
          <div className="w-64 border-r bg-sidebar animate-in fade-in duration-200">
            <NavPanel 
              title={activeItem.panelTitle || activeItem.label}
              className="bg-sidebar"
            >
              {activeItem.panel}
            </NavPanel>
          </div>
        );
      })()}

      {/* Hover Panel */}
      {hoverSidenavId && (() => {
        const hoveredItem = items.find(item => item.id === hoverSidenavId);
        if (!hoveredItem?.panel) return null;

        return (
          <div 
            className="absolute left-16 top-0 bottom-0 pl-2 z-50"
            onMouseEnter={() => handleItemMouseEnter(hoveredItem)}
            onMouseLeave={handleMouseLeave}
          >
            <div className="h-full animate-in fade-in slide-in-from-left-1 duration-200 bg-background border rounded-lg shadow-lg" 
              style={{ width: hoveredItem.panelWidth || '16rem' }}
            >
              <NavPanel 
                title={hoveredItem.panelTitle || hoveredItem.label}
                className="bg-background h-full rounded-lg"
              >
                {hoveredItem.panel}
              </NavPanel>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

interface NavRailItemProps {
  label: string;
  isActive?: boolean;
  badge?: React.ReactNode;
  disabled?: boolean;
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
  target?: string;
  rel?: string;
  icon?: React.ReactNode;
}

export function NavRailItem({
  label,
  isActive,
  badge,
  disabled,
  href,
  onClick,
  target,
  rel,
  icon
}: NavRailItemProps) {
  const itemContent = (
    <div
      className={cn(
        'w-12 h-12 rounded-lg flex flex-col items-center justify-center relative gap-1 py-1',
        'hover:bg-accent/50 transition-colors',
        isActive ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'hover:bg-accent/50',
        disabled && 'opacity-50'
      )}
    >
      {icon || <FileSpreadsheet className="w-5 h-5" />}
      <span className="text-[10px] font-medium leading-none text-center">{label}</span>
      {badge && (
        <div className="absolute top-0 right-0 -mt-1 -mr-1">
          {badge}
        </div>
      )}
    </div>
  );

  if (href && !disabled) {
    return (
      <Link
        href={href}
        onClick={onClick}
        target={target}
        rel={rel}
        className={cn(
          'block',
          disabled && 'pointer-events-none'
        )}
      >
        {itemContent}
      </Link>
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'block w-full',
        disabled && 'pointer-events-none'
      )}
    >
      {itemContent}
    </button>
  );
}

// Helper function to render badge
function renderBadge(item: NavItem) {
  if (!item.badgeCount || item.badgeCount <= 0) return null;
  
  return (
    <span className={cn(
      'absolute top-0 right-0 -mt-1 -mr-1 flex h-4 min-w-4 items-center justify-center rounded-full text-[10px] font-medium',
      item.badgeVariant === 'primary' ? 'bg-primary text-primary-foreground' : 
      item.badgeVariant === 'secondary' ? 'bg-secondary text-secondary-foreground' :
      item.badgeVariant === 'destructive' ? 'bg-destructive text-destructive-foreground' :
      'bg-muted-foreground text-background'
    )}>
      {item.badgeCount > 99 ? '99+' : item.badgeCount}
    </span>
  );
}

export interface NavPanelProps {
  title?: string;
  onClose?: () => void;
  children: React.ReactNode;
  className?: string;
}

/**
 * A side panel component for NavRail
 */
export function NavPanel({
  title,
  onClose,
  children,
  className
}: NavPanelProps) {
  return (
    <div className={cn('w-64 border-r bg-sidebar h-full flex flex-col', className)}>
      {/* Panel header */}
      {(title || onClose) && (
        <div className="p-3 flex items-center justify-between border-b shrink-0">
          <div className="flex items-center gap-3">
            {title && <h2 className="font-medium text-sm">{title}</h2>}
          </div>
          {onClose && (
            <button 
              onClick={onClose}
              className="p-1 rounded-full hover:bg-sidebar-accent text-muted-foreground hover:text-sidebar-accent-foreground"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18" /><path d="m6 6 12 12" />
              </svg>
            </button>
          )}
        </div>
      )}
      
      {/* Panel content */}
      <div className="flex-1 overflow-y-auto">{children}</div>
    </div>
  );
}

export interface Dataset {
  id: string;
  name: string;
  tables: Table[];
}

export interface Table {
  id: string;
  name: string;
}

export interface ViewUrl {
  id: string;
  name: string;
}

export interface ActiveTableId {
  id: string;
}

export function DatasetPanel({
  dataset,
  viewUrl,
  activeTableId
}: {
  dataset: Dataset;
  viewUrl: ViewUrl;
  activeTableId: ActiveTableId;
}) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col space-y-1 p-2 flex-1 overflow-y-auto">
        {dataset.tables.map(table => (
          <Link
            key={table.id}
            href={`${viewUrl.id}?table=${table.id}`}
            className={cn(
              "flex items-center gap-2 px-3 py-2 text-sm rounded-md w-full text-left transition-colors mb-1",
              table.id === activeTableId.id 
                ? "bg-secondary text-secondary-foreground" 
                : "hover:bg-secondary/10"
            )}
          >
            <FileSpreadsheet className="h-4 w-4 shrink-0" />
            <span className="truncate" title={table.name}>{table.name}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}